// Bundle-level AI summary for a queued-edits proposal, stored as an extension
// on the Bundle itself so the proposer (AI/backend) can generate and persist
// it alongside the bundle — no side-channel map. One summary for the whole
// proposal (per-entry blurbs proved redundant with the rendered diffs).
// Bundle is not a DomainResource (no top-level `extension`), so it lives on
// `Bundle.meta.extension`:
//
//   Bundle.meta.extension: [{
//     url: QUEUED_EDITS_AI_SUMMARY_EXTENSION,
//     valueString: <human-readable summary of all proposed changes>,
//   }]
//
// This module also owns the model-facing view of a proposal for summary
// generation (`describeBundleEntriesForAiSummary`) and the shared system
// prompt (`queuedEditsAiSummarySystemPrompt`), so the backend generator
// (@seen/functions) and the portal's refresh endpoint interpret the bundle —
// summarize the artifact, never the proposing agent's stated intent —
// identically.

import type { Bundle } from '@medplum/fhirtypes'
import {
  QUEUED_EDITS_AI_SUMMARY_EXTENSION,
  QUEUED_EDITS_AI_SUMMARY_OMITTED_EXTENSION,
} from '#src/codeSystems.ts'
import { getDisplayString } from '#src/display.ts'
import { base64Decode } from '#src/encoding.ts'
import {
  isFhirPathPatchParameters,
  parametersToFhirPathPatchOperations,
} from '#src/fhirpath-patch/index.ts'
import { entryHandle } from '#src/queued-edits/entry-handle.ts'

// Replaces the bundle's AI-summary extension. Pass undefined to clear it.
// Mutates and returns the bundle.
export function setBundleAiSummary(
  bundle: Bundle,
  summary: string | undefined
): Bundle {
  const others = (bundle.meta?.extension ?? []).filter(
    (ext) => ext.url !== QUEUED_EDITS_AI_SUMMARY_EXTENSION
  )
  const extension = summary
    ? [
        ...others,
        { url: QUEUED_EDITS_AI_SUMMARY_EXTENSION, valueString: summary },
      ]
    : others
  if (extension.length > 0) {
    bundle.meta = { ...bundle.meta, extension }
  } else if (bundle.meta?.extension) {
    delete bundle.meta.extension
    if (Object.keys(bundle.meta).length === 0) {
      delete bundle.meta
    }
  }
  return bundle
}

export function getBundleAiSummary(bundle: Bundle): string | undefined {
  return bundle.meta?.extension?.find(
    (ext) => ext.url === QUEUED_EDITS_AI_SUMMARY_EXTENSION
  )?.valueString
}

// Record which entries the current summary + intents intentionally omitted —
// entries whose proposed change was already applied outside the review by the
// time the summary was (re)generated. Stored as a JSON array of entry handles
// on `Bundle.meta.extension`. Pass an empty array to clear (the summary covers
// every entry — the same meaning as the extension being absent). Mutates and
// returns the bundle.
export function setBundleAiSummaryOmittedEntries(
  bundle: Bundle,
  handles: string[]
): Bundle {
  const others = (bundle.meta?.extension ?? []).filter(
    (ext) => ext.url !== QUEUED_EDITS_AI_SUMMARY_OMITTED_EXTENSION
  )
  const extension =
    handles.length > 0
      ? [
          ...others,
          {
            url: QUEUED_EDITS_AI_SUMMARY_OMITTED_EXTENSION,
            valueString: JSON.stringify([...handles].sort()),
          },
        ]
      : others
  if (extension.length > 0) {
    bundle.meta = { ...bundle.meta, extension }
  } else if (bundle.meta?.extension) {
    delete bundle.meta.extension
    if (Object.keys(bundle.meta).length === 0) {
      delete bundle.meta
    }
  }
  return bundle
}

// Bound the AI-summary prompt — past this, remaining entries are not shown to
// the model (the summary notes nothing beyond what the included entries show).
export const MAX_AI_SUMMARY_ENTRIES = 200

export interface AiSummaryEntryDescription {
  target: string
  method: string
  resource?: string
  ops?: unknown
}

// Compact, self-describing view of a bundle entry for the summary prompt —
// derived mechanically from the bundle itself (the target reference + decoded
// patch ops, plus the resource display for create/update bodies that carry a
// full resource), so the model interprets the proposed changes directly
// instead of the caller re-describing them per resource type. PATCH entries no
// longer embed their base resource, so they contribute target + ops only.
// `omit` skips entries by handle — used when regenerating after some entries
// went stale so the summary describes only the entries still under review.
export function describeBundleEntriesForAiSummary(
  bundle: Bundle,
  omit: Set<string> = new Set()
): AiSummaryEntryDescription[] {
  const items: AiSummaryEntryDescription[] = []
  for (const entry of bundle.entry ?? []) {
    const method = entry.request?.method
    // Handle each entry under the same scheme the review UI, intents, and
    // decisions use (entryHandle), so `omit` — populated from the client's
    // dropped-entry handles — matches. A prior bare-`request.url` target
    // mismatched conditional-create POSTs (which the client keys by fullUrl)
    // and collided across same-type POSTs.
    const target = entryHandle(entry)
    if (!method || !target || omit.has(target)) {
      continue
    }
    const resource = entry.resource
    if (isFhirPathPatchParameters(resource)) {
      items.push({
        target,
        method,
        ops: parametersToFhirPathPatchOperations(resource).map((op) => ({
          type: op.type,
          path: op.path,
          name: op.name,
          value: op.value?.value,
        })),
      })
      continue
    }
    if (resource?.resourceType === 'Binary' && resource.data) {
      let ops: unknown
      try {
        ops = (
          JSON.parse(base64Decode(resource.data)) as { op: string }[]
        ).filter((op) => op.op !== 'test')
      } catch {
        ops = undefined
      }
      items.push({ target, method, ops })
      continue
    }
    items.push({
      target,
      method,
      resource: resource ? getDisplayString(resource) : undefined,
    })
  }
  return items
}

// The shared system prompt for generating a proposal's bundle-level summary
// and per-entry intents in one model call. `context` is the domain framing —
// why these changes are being proposed.
export function queuedEditsAiSummarySystemPrompt(context: string): string {
  return (
    'You write plain-language descriptions of proposed changes to a clinical record for non-technical PACE staff reviewing them. ' +
    'Each input item is one entry of a proposed FHIR change bundle: its `target` (an opaque handle), HTTP method, the affected resource display when known, and the decoded patch operations. ' +
    `${context} ` +
    'Return two things. ' +
    '`summary`: one overview (2-3 sentences) covering the whole set — what kinds of records are affected, how many of each, and what happens to them. ' +
    '`intents`: one entry per input item, each with the item\'s `target` echoed back verbatim and a short `intent` — a single plain phrase (≤6 words, e.g. "Cancel the lab order") naming what that one entry does. ' +
    'Do not use FHIR terminology. Do not invent details beyond what the items show.'
  )
}

// The entry handles the current summary intentionally omitted (sorted), or []
// when the summary covers every entry.
export function getBundleAiSummaryOmittedEntries(bundle: Bundle): string[] {
  const raw = bundle.meta?.extension?.find(
    (ext) => ext.url === QUEUED_EDITS_AI_SUMMARY_OMITTED_EXTENSION
  )?.valueString
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((h): h is string => typeof h === 'string').sort()
      : []
  } catch {
    return []
  }
}
